import React, { useState, useRef } from 'react';
import { useNavigate, Routes, Route, useLocation } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';

// Import components
import Hero from './components/Hero';
import ProfileCard from './components/ProfileCard';
import Services from './components/Services';
import Testimonials from './components/Testimonials';
import About from './components/About';
import FunFacts from './components/FunFacts';
import VideoSection from './components/VideoSection';
import Newsletter from './components/Newsletter';
import Header from './components/Header';
import Footer from './components/Footer';
import Blog from './components/Blog';
import BlogDetails from './components/BlogDetails';
import NewSpecialModal from './components/NewSpecialModal';
import GoogleReviews from './components/GoogleReviews';

// Import utils
import { renderStars, renderSocialLinks, renderExpertiseTags, scroll, renderBookingButton } from './utils';

const PublicPreviewHT = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading] = useState(false);
  const [error] = useState(null);
  const [showFullAbout, setShowFullAbout] = useState(false);
  
  // Refs for scroll containers
  const servicesScrollRef = useRef(null);
  const testimonialsScrollRef = useRef(null);
  
  // Mock data for Coach HT
  const websiteData = {
    name: "Coach HT",
    title: "PHYTness Professional | Nutrition Coach",
    profession: "Nutrition & Fitness Coach",
    bio: "I make working out fun and effective. Don't waste your time. Coach HT will get you to your health and fitness goals twice as fast using the PHYT method. A method focused on proper form and alignment. This means you will spend less time working and see more results.",
    profileImage: "/coach_ht.png",
    location: "Los Angeles, CA",
    email: "coach@thephytgym.com",
    phone: "4242551338",
    website: "https://www.thephytgym.com/",
    socialLinks: {
      instagram: "thephytgym",
      youtube: "@thephytgym",
      tiktok: "thephytgym"
    },
    expertiseAreas: [
      "Nutrition Coaching",
      "Fitness Training",
      "PHYT Method",
      "Fat Loss",
      "Form & Alignment"
    ],
    stats: [
      { number: "8+", label: "Years Experience" },
      { number: "500+", label: "Clients Trained" },
      { number: "95%", label: "Success Rate" },
      { number: "1000+", label: "Transformations" }
    ],
    yearsExperience: "8",
    services: [
      {
        id: '1',
        title: 'New Client Special',
        description: 'Get started with 4 personalized training sessions at a special rate. Perfect for new clients looking to begin their fitness journey.',
        duration: '4 sessions',
        price: '$380',
        originalPrice: '$520',
        icon: '🎁',
        features: [
          'Personalized training program',
          'Form and technique assessment',
          'Nutrition guidance included',
          'Flexible scheduling',
          'Limited time offer'
        ],
        link: 'https://www.thephytgym.com/products/gift-phytraining-4-pack'
      },
      {
        id: '2',
        title: 'Small Group Training',
        description: 'Join our small group training sessions for a more affordable yet effective workout experience. Perfect for those who enjoy group motivation.',
        duration: 'Group Sessions',
        price: '$135',
        originalPrice: '$180',
        icon: '👥',
        features: [
          'Small group setting',
          'Expert guidance',
          'Motivating atmosphere',
          'Regular schedule',
          'New client discount'
        ],
        link: 'https://www.thephytgym.com/products/group-training-jan-30-to-feb-20'
      },
      {
        id: '3',
        title: 'PHYT FOOD Program',
        description: 'Lose 1 inch in your waist with our comprehensive meal planning and nutrition program. Learn sustainable eating habits for life.',
        duration: 'Ongoing',
        price: '$200',
        icon: '🍎',
        features: [
          'Personalized meal plans',
          'Nutrition education',
          'Recipe database',
          'Shopping guides',
          'Ongoing support'
        ],
        link: 'https://www.thephytgym.com/products/phyt-food-2-0'
      },
      {
        id: '4',
        title: 'PHYT Travel Kit',
        description: 'Stay mobile and flexible with our 5-minute mobility program. Perfect for busy professionals and travelers.',
        duration: '5 min sessions',
        price: '$9.99',
        icon: '✈️',
        features: [
          'Quick mobility routines',
          'Travel-friendly exercises',
          'Digital access',
          'No equipment needed',
          'Instant access'
        ],
        link: 'https://my.playbookapp.io/coach-ht/programs/phyt-travel-kit-mobility-in-5-min/26819'
      }
    ],
    testimonials: [
      {
        id: '1',
        name: 'Daisy Gomez',
        content: "Heather (HT) she is the best trainer coach I've ever had! She is so knowledgeable in everything training and working out! She is 4x certified! I was the person that has never worked out and she turned me into a phytness lover! Slowly but surely. She focuses on all things woman phyness! If you don't know where to start.. it is here!!!",
        rating: 5,
        result: 'Found Perfect Coach'
      },
      {
        id: '2',
        name: 'Meredith Smith',
        content: "I have been a client of Heather since 2017. From her bootcamp days to her current about the PHYT, Heather's growth and expertise has been amazing. Using her weight loss regimen, I was able to lose 25 pounds. After 3 years, I'm still maintaining the weight loss.",
        rating: 5,
        result: 'Lost 25 lbs'
      },
      {
        id: '3',
        name: 'Ribkah N',
        content: "Heather is INCREDIBLE! I've been working with her for two months and the changes are extraordinary - my mobility, flexibility, and strength have progressed so much. Heather truly cares about her clients and is always going to put your safety first and support you in building a strong foundation.",
        rating: 5,
        result: 'Improved Mobility'
      },
      {
        id: '4',
        name: 'J S',
        content: "Love love love the Phyt Gym! Been a member since 2021 and got a whole new shape through Coach HT's fitness and diet programs. HT focuses on safety first, making weight training doable for all without injury. I've done both individual coaching and small group training - you can't go wrong with either.",
        rating: 5,
        result: 'Transformed Body'
      }
    ],
    calendarBookingPage: "https://www.thephytgym.com/products/group-training-jan-30-to-feb-20",
    showPricing: true
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-purple-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-purple-50 flex flex-col items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-purple-800 mb-2">Profile Unavailable</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg shadow-sm hover:bg-purple-700 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Check if we're on a blog details page
  const isBlogDetailsPage = location.pathname.startsWith('/blog/');

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white overflow-x-hidden pt-10">
      <Header />
      <NewSpecialModal />
      <GoogleReviews />
      
      <Routes>
        <Route path="/blog/:id" element={
          <BlogDetails post={location.state?.post} />
        } />
        <Route path="*" element={
          <>
            <Hero websiteData={websiteData} />
            <main className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-16">
              <div className="grid grid-cols-1 md:grid-cols-[400px_1fr] lg:grid-cols-[450px_1fr] gap-6 lg:gap-8">
                <div className="w-full">
                  <ProfileCard 
                    websiteData={websiteData}
                    renderExpertiseTags={() => renderExpertiseTags(websiteData.expertiseAreas)}
                    renderSocialLinks={() => renderSocialLinks(websiteData.socialLinks)}
                    renderBookingButton={() => renderBookingButton(websiteData.calendarBookingPage)}
                  />
                  
                  <VideoSection />
                  
                  <FunFacts />
                </div>
                
                <div className="w-full min-w-0 overflow-hidden">
                  <div id="about" className="w-full">
                    <About 
                      showFullAbout={showFullAbout}
                      setShowFullAbout={setShowFullAbout}
                    />
                  </div>

                  <div id="services" className="mb-10 mt-10 w-full">
                    <Services 
                      websiteData={websiteData}
                      servicesScrollRef={servicesScrollRef}
                      scroll={scroll}
                    />
                  </div>
              
                  <div id="testimonials" className="mb-10 w-full">
                    <Testimonials 
                      websiteData={websiteData}
                      testimonialsScrollRef={testimonialsScrollRef}
                      scroll={scroll}
                      renderStars={renderStars}
                    />
                  </div>

                </div>
              </div>
            </main>
            
            {/* Full width sections */}
            <div className="w-full">
              <div id="blog" className="mb-10 px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
                <Blog />
              </div>
              
              <div id="newsletter" className="px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
                <Newsletter />
              </div>

              <div id="booking" className="mt-10 px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
                {renderBookingButton(websiteData.calendarBookingPage)}
              </div>
            </div>
          </>
        } />
      </Routes>

      {/* Custom CSS for background pattern and scrollbar hiding */}
      <style>{`
        .bg-pattern {
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }
        
        .hide-scrollbar {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      
      {/* Back to top button */}
      {!isBlogDetailsPage && (
        <button 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-4 right-4 p-3 rounded-full bg-purple-600 text-white shadow-lg hover:bg-purple-700 transition-colors"
          aria-label="Back to top"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      )}
      <Footer />
    </div>
  );
};

export default PublicPreviewHT;